import React, { useState } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { Modal } from '@els/els-react--modal';
import { ButtonWithIcon } from '@els/els-react--button';
import ResultTracking from '@Services/ResultTracking';
import SubstanceDetailsModal from '@Components/Results/structure/substance-details/SubstanceDetailsModal';
import HoverTooltip from '@Components/HoverTooltip';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureToolbarFlyout from '@Components/Results/structure/StructureToolbarFlyout';

import './SubstanceDetails.scss';

function SubstanceDetails({ trackingContext, structure, reactionId, tooltipId, metadata, trackingContent, gridView = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipLabel = 'Show Details';

  const trackOpen = () => {
    ResultTracking.structureContentInteraction('view details', trackingContext, trackingContent);
    const tooltip = document.getElementById(tooltipId);
    if (tooltip) {
      tooltip.dispatchEvent(
        new MouseEvent('mouseleave', {
          bubbles: true,
        })
      );
    }
  };

  const toggleDetails = (open) => {
    setIsOpen(open);
    if (open) {
      trackOpen();
    }
  };

  const closeDetails = () => setIsOpen(false);

  const detailsTrigger = (
    <HoverTooltip id={tooltipId} tooltip={tooltipLabel} tooltipPlace="top" theme="simple">
      <span id="view-details-button-description" className="sr-only">
        Overview of substance details with a link to the full record.
      </span>
      <ButtonWithIcon
        data-e2e="substance-details-link"
        sprite="TextFile"
        onClick={gridView ? () => toggleDetails(true) : noop}
        iconOnly
        type="borderless"
        aria-expanded={isOpen}
        aria-describedby="view-details-button-description">
        View Details
      </ButtonWithIcon>
    </HoverTooltip>
  );

  // Grid view: render the details overview in a side modal panel instead of a flyout.
  if (gridView) {
    return (
      <>
        {detailsTrigger}
        {isOpen && (
          <Modal className="substance-details-modal-panel" label={tooltipLabel} panel panelPosition="right" onClose={closeDetails}>
            <SubstanceDetailsModal
              isOpen={isOpen}
              structure={structure}
              reactionId={reactionId}
              closeMenu={closeDetails}
              metadata={metadata}
            />
          </Modal>
        )}
      </>
    );
  }

  return (
    <StructureToolbarFlyout
      isControlled
      isOpen={isOpen}
      modalLabel="Substance details"
      flyout={<SubstanceDetailsModal isOpen={isOpen} structure={structure} reactionId={reactionId} closeMenu={noop} metadata={metadata} />}
      shouldCloseOnESC
      showCloseButton
      onToggle={toggleDetails}
      placement="bottom-start"
      className="structure-toolbar-options"
      pointerType="primary">
      {detailsTrigger}
    </StructureToolbarFlyout>
  );
}

SubstanceDetails.propTypes = {
  trackingContext: PropTypes.string,
  structure: PropTypes.object.isRequired,
  reactionId: PropTypes.string,
  tooltipId: PropTypes.string,
  metadata: MetadataPropTypesShape,
  trackingContent: PropTypes.array,
  gridView: PropTypes.bool,
};

export default SubstanceDetails;
